/**
 * Dev-only mock backend for the admin console.
 *
 * Enabled when localStorage `rp.mock` === "1" (toggled by the floating
 * MockPanel). It monkeypatches `ApiClient.prototype.request` — every service
 * call routes through it — to return role-aware fixtures, and writes a fake
 * (unsigned, never sent to a real server) session JWT so `useRoles()` reflects
 * the chosen role. Nothing ships to production: installAdminMocks() no-ops
 * unless the flag is set, and the panel mounts only under import.meta.env.DEV.
 *
 * Role (localStorage `rp.mock.role`): super_admin | brokerage_admin | agent.
 */
import { ApiClient, ApiClientError } from "@re-platform/api-client";
import type { DocType, DossierTemplate } from "@re-platform/types";
import type {
  AgentCostResponse,
  CostTimeseriesResponse,
  MarginResponse,
  PlatformCostResponse,
  BrokerageSettings,
  StaffSeats,
  Subscription,
} from "@re-platform/api-client";

type Role = "super_admin" | "brokerage_admin" | "agent";

const MOCK_BROKERAGE: BrokerageSettings = {
  brokerage_type: "REAL_ESTATE",
  display_name: "Lone Star Realty",
  status: "ACTIVE",
  custom_domain: "app.lonestar.example",
  custom_domain_verified: true,
  legal_name: "Lone Star Realty LLC",
  trec_license_number: "480091",
  // The IABS names these two (V082). The supervisor is deliberately null:
  // that is the common case, and it exercises the fallback to the broker.
  designated_broker_name: "Dana Whitfield",
  designated_broker_license_no: "480091",
  // V092 — the broker's OWN contact details, deliberately different from the
  // firm's support line below: a mock where they match cannot show whether the
  // notice is drawing from the right source.
  designated_broker_email: "dana@lonestar.example",
  designated_broker_phone: "+15125550111",
  supervising_license_holder: null,
  supervising_license_no: null,
  // Unset, matching the supervisor's name above — the whole row falls back to
  // the designated broker when nobody is named.
  supervising_email: null,
  supervising_phone: null,
  nmls_id: null,
  support_email: "hello@lonestar.example",
  primary_phone: "+15125550142",
  address_street: "1200 S Congress Ave",
  address_city: "Austin",
  address_state: "TX",
  address_zip: "78704",
  market_area: "Greater Austin",
  county: "Travis",
  purchase_fee_pct: "3.00",
  lease_fee_pct: "100.00",
  // TXR 1102 ¶5.E (V093), and deliberately NULL — the state every existing
  // brokerage is in the moment the migration lands, since the columns were added
  // nullable with no backfill. Under D28 that now means INHERIT (lease fee and
  // purchase fee respectively), so this is also the common steady state, not
  // just a transitional one. A mock that pre-filled them would show neither.
  renewal_fee_pct: null,
  sale_fee_pct: null,
  protection_period_days: 180,
  representation_term_months: 6,
  primary_color: "#4f46e5",
  secondary_color: "#4a5b6c",
  logo_s3_key: null,
  logo_url: null,
};

const MOCK_SEATS: StaffSeats = {
  agents: { used: 4, limit: 10 },
  admins: { used: 1, limit: 3 },
};

const MOCK_PLATFORM_COSTS: PlatformCostResponse = {
  since: "2026-07-01",
  until: "2026-07-31",
  platform_total_aws: "412.55",
  platform_total_usage: "871.45",
  platform_total: "1284.00",
  by_tenant: [],
};

const MOCK_AGENT_COSTS: AgentCostResponse = {
  tenant_id: "mock-tenant",
  since: "2026-07-01",
  until: "2026-07-31",
  total_usage: "871.45",
  rows: [],
};

const MOCK_TIMESERIES: CostTimeseriesResponse = {
  since: "2026-07-01",
  until: "2026-07-31",
  granularity: "daily",
  points: [],
};

export function mockEnabled(): boolean {
  try {
    return localStorage.getItem("rp.mock") === "1";
  } catch {
    return false;
  }
}

export function mockRole(): Role {
  try {
    return (localStorage.getItem("rp.mock.role") as Role) || "brokerage_admin";
  } catch {
    return "brokerage_admin";
  }
}

// ---- fake JWT so useRoles()/rolesFromToken() see the chosen role ------------

const b64url = (s: string) =>
  btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

export function makeFakeToken(role: Role): string {
  const identity =
    role === "agent"
      ? { name: "Jordan Alvarez", email: "jordan@lonestar.example" }
      : role === "super_admin"
        ? { name: "Alex Chen", email: "alex@platform.example" }
        : { name: "Brenda Cole", email: "brenda@lonestar.example" };
  const header = b64url(JSON.stringify({ alg: "none", typ: "JWT" }));
  const payload = b64url(
    JSON.stringify({
      sub: "mock-staff",
      "https://re-platform/roles": [role],
      primary_role: role,
      tenant_id: TENANT_ID,
      ...identity,
    }),
  );
  return `${header}.${payload}.mock`;
}

// ---- fixture helpers --------------------------------------------------------

const TENANT_ID = "30000000-0000-0000-0000-000000000001";
const AGENT_ID = "20000000-0000-0000-0000-000000000001";
const days = (n: number) => new Date(Date.now() + n * 86_400_000).toISOString();

/** Spec 023 — `rp.mock.dual` = "1" makes the agent dual-licensed: a second,
 *  mortgage brokerage membership, as identity-svc's /memberships returns it. */
const SECOND_TENANT_ID = "30000000-0000-0000-0000-000000000002";
function mockDual(): boolean {
  try {
    return localStorage.getItem("rp.mock.dual") === "1";
  } catch {
    return false;
  }
}

function memberships(role: Role) {
  if (role === "agent" && mockDual())
    return [
      {
        tenant_id: TENANT_ID,
        slug: "lonestar",
        display_name: "Lone Star Realty",
        brokerage_type: "REAL_ESTATE",
        role,
        auth0_org_id: null,
      },
      {
        tenant_id: SECOND_TENANT_ID,
        slug: "hillcountry",
        display_name: "Hill Country Lending",
        brokerage_type: "MORTGAGE",
        role,
        auth0_org_id: null,
      },
    ];
  if (role === "super_admin")
    return [
      {
        tenant_id: TENANT_ID,
        slug: "platform",
        display_name: "Platform HQ",
        brokerage_type: "PLATFORM",
        role: "super_admin",
        auth0_org_id: null,
      },
    ];
  return [
    {
      tenant_id: TENANT_ID,
      slug: "lonestar",
      display_name: "Lone Star Realty",
      brokerage_type: "REAL_ESTATE",
      role,
      auth0_org_id: null,
    },
  ];
}

let SUBSCRIPTIONS: Subscription[] = [
  {
    id: "sub-1",
    tenant_id: null,
    tenant_name: null,
    vendor: "ElevenLabs",
    name: "Voice — Starter",
    category: "voice",
    amount: "22.00",
    currency: "USD",
    billing_cycle: "MONTHLY",
    active_from: days(-300).slice(0, 10),
    active_to: null,
    notes: null,
    monthly_equivalent: "22.00",
  },
  {
    id: "sub-2",
    tenant_id: null,
    tenant_name: null,
    vendor: "Twilio",
    name: "Programmable Voice + SMS",
    category: "voice",
    amount: "140.00",
    currency: "USD",
    billing_cycle: "MONTHLY",
    active_from: days(-240).slice(0, 10),
    active_to: null,
    notes: "Usage-based; amount is the running monthly average.",
    monthly_equivalent: "140.00",
  },
  {
    id: "sub-3",
    tenant_id: null,
    tenant_name: null,
    vendor: "DocuSign",
    name: "Business Pro — 5 seats",
    category: "e-sign",
    amount: "1560.00",
    currency: "USD",
    billing_cycle: "ANNUAL",
    active_from: days(-180).slice(0, 10),
    active_to: null,
    notes: null,
    monthly_equivalent: "130.00",
  },
  {
    id: "sub-4",
    tenant_id: null,
    tenant_name: null,
    vendor: "Postmark",
    name: "Transactional email",
    category: "email",
    amount: "15.00",
    currency: "USD",
    billing_cycle: "MONTHLY",
    active_from: days(-260).slice(0, 10),
    active_to: null,
    notes: null,
    monthly_equivalent: "15.00",
  },
  {
    id: "sub-5",
    tenant_id: TENANT_ID,
    tenant_name: "Lone Star Realty",
    vendor: "Twilio",
    name: "Dedicated DID block",
    category: "voice",
    amount: "18.00",
    currency: "USD",
    billing_cycle: "MONTHLY",
    active_from: days(-90).slice(0, 10),
    active_to: null,
    notes: "Charged wholly to this brokerage.",
    monthly_equivalent: "18.00",
  },
];

const MARGIN_TENANTS: {
  tenant_id: string;
  display_name: string;
  plan_type: string;
  revenue: number;
  usage: number;
}[] = [
  {
    tenant_id: TENANT_ID,
    display_name: "Lone Star Realty",
    plan_type: "growth",
    revenue: 1980,
    usage: 611,
  },
  {
    tenant_id: "30000000-0000-0000-0000-000000000002",
    display_name: "Bluebonnet Mortgage",
    plan_type: "starter",
    revenue: 1240,
    usage: 902,
  },
  {
    tenant_id: "30000000-0000-0000-0000-000000000003",
    display_name: "Hill Country Homes",
    plan_type: "growth",
    revenue: 860,
    usage: 244,
  },
  {
    tenant_id: "30000000-0000-0000-0000-000000000004",
    display_name: "Gulf Coast Lending",
    plan_type: "starter",
    revenue: 415,
    usage: 388,
  },
];

/** Scale the fixture by how long the requested window is, so switching Day /
 *  Week / Month / Year in the chart moves the bars instead of redrawing the
 *  same picture. Deterministic — no random, or every refetch would jitter. */
function marginFor(query: unknown): MarginResponse {
  const q = (query ?? {}) as { since?: string; until?: string };
  const since = q.since ? new Date(q.since) : new Date();
  const until = q.until ? new Date(q.until) : new Date();
  const days = Math.max(
    1,
    Math.round((until.getTime() - since.getTime()) / 86_400_000),
  );
  const scale = days / 30;
  // Older periods ran smaller — a flat history reads as fake.
  const monthsBack = Math.max(
    0,
    Math.round((Date.now() - until.getTime()) / (30 * 86_400_000)),
  );
  const decay = Math.pow(0.93, monthsBack);
  const money = (n: number) => (n * scale * decay).toFixed(2);

  const rows = MARGIN_TENANTS.map((t) => {
    const revenue = Number(money(t.revenue));
    const usage = Number(money(t.usage));
    const aws = usage * 0.42;
    // Pro-rata share of the platform-wide subscriptions plus any of its own —
    // roughly what the server computes, so the chart's proportions are honest.
    const subs = usage * 0.18;
    const margin = revenue - aws - subs;
    return {
      tenant_id: t.tenant_id,
      display_name: t.display_name,
      plan_type: t.plan_type,
      revenue: revenue.toFixed(2),
      aws_cost: aws.toFixed(2),
      usage_cost: usage.toFixed(2),
      subscription_cost: subs.toFixed(2),
      margin: margin.toFixed(2),
      margin_pct: revenue ? (margin / revenue).toFixed(4) : null,
      at_risk: revenue > 0 && margin / revenue < 0.25,
    };
  });
  const sum = (f: (r: (typeof rows)[number]) => string) =>
    rows.reduce((acc, r) => acc + Number(f(r)), 0).toFixed(2);

  return {
    since: q.since ?? "",
    until: q.until ?? "",
    risk_threshold: "0.25",
    platform_revenue: sum((r) => r.revenue),
    platform_aws_cost: sum((r) => r.aws_cost),
    platform_subscription_cost: sum((r) => r.subscription_cost),
    platform_margin: sum((r) => r.margin),
    rows,
  };
}

// Dossier templates — two platform defaults and one tenant fork, so the screen
// shows both scopes and the "Fork for my brokerage" button has a row it applies
// to. Typed, so a shape drift breaks the build rather than the page.
const DOC_TYPES: DocType[] = [
  ["government_id", "Government ID", "identity"],
  ["pay_stub", "Pay stub", "income"],
  ["bank_statement", "Bank statement", "assets"],
  ["profit_loss", "Profit & loss", "income"],
  ["tax_return", "Tax return", "income"],
].map(([key, name, category], i) => ({
  id: `dt-${i + 1}`,
  doc_type_key: key!,
  display_name: name!,
  category: category!,
  textract_mode: "FORMS",
  required_fields: [],
  supported_formats: ["pdf", "jpg", "png"],
  auto_populate_form: null,
  active: true,
  created_at: days(-300),
  updated_at: days(-300),
}));

function slot(
  n: number,
  key: string,
  name: string,
  required: boolean,
  recency: number | null,
  conditional: Record<string, unknown> | null = null,
): DossierTemplate["items"][number] {
  return {
    id: `ti-${key}-${n}`,
    doc_type_id: DOC_TYPES.find((d) => d.doc_type_key === key)?.id ?? "dt-1",
    doc_type_key: key,
    doc_type_display_name: name,
    is_required: required,
    conditional_on: conditional,
    recency_days: recency,
    display_order: n,
  };
}

const TEMPLATES: DossierTemplate[] = [
  {
    id: "tpl-1",
    tenant_id: null,
    scope: "platform",
    template_key: "buy_mortgage_v2",
    flow_type: "MORTGAGE",
    display_name: "Buy + Mortgage",
    description: "Buyer rep + financing checklist",
    reminder_config: {},
    active: true,
    created_at: days(-300),
    updated_at: days(-40),
    items: [
      slot(0, "government_id", "Government ID", true, null),
      slot(1, "pay_stub", "Pay stub", true, 30),
      slot(2, "bank_statement", "Bank statement", true, 60),
      slot(3, "profit_loss", "Profit & loss", false, null, {
        field: "client.employment_status",
        equals: "self_employed",
      }),
      slot(4, "tax_return", "Tax return", false, 365),
    ],
  },
  {
    id: "tpl-2",
    tenant_id: TENANT_ID,
    scope: "tenant",
    template_key: "seller_listing_v1",
    flow_type: "REAL_ESTATE",
    display_name: "Seller listing",
    description: "Listing agreement + disclosures",
    reminder_config: {},
    active: true,
    created_at: days(-120),
    updated_at: days(-9),
    items: [slot(0, "government_id", "Government ID", true, null)],
  },
  {
    id: "tpl-3",
    tenant_id: null,
    scope: "platform",
    template_key: "pre_approval_v1",
    flow_type: "MORTGAGE",
    display_name: "Pre-approval",
    description: "Minimum set for a pre-approval letter",
    reminder_config: {},
    active: true,
    created_at: days(-300),
    updated_at: days(-80),
    items: [
      slot(0, "government_id", "Government ID", true, null),
      slot(1, "pay_stub", "Pay stub", true, 30),
    ],
  },
];

/**
 * GET /admin/staff/members → StaffMember[].
 *
 * `status` is "ACTIVE" | "SUSPENDED" (admin-svc schemas/staff.py is a Literal
 * on exactly those). Lowercase here missed the badge's colour lookup entirely,
 * so every staff pill rendered grey and lowercase while the real API's rendered
 * green and amber — a difference visible only against a real backend.
 */
const STAFF = [
  {
    id: "u-1",
    email: "brenda@lonestar.example",
    full_name: "Brenda Cole",
    phone_e164: "+15125550100",
    primary_role: "brokerage_admin",
    status: "ACTIVE",
    last_login_at: days(-1),
    created_at: days(-200),
    agent_id: null,
    license_count: 0,
    nmls_id: null,
    trec_license_number: null,
  },
  {
    id: "u-2",
    email: "jordan@lonestar.example",
    full_name: "Jordan Alvarez",
    phone_e164: "+15125550142",
    primary_role: "agent",
    status: "ACTIVE",
    last_login_at: days(-2),
    created_at: days(-150),
    agent_id: AGENT_ID,
    license_count: 1,
    nmls_id: null,
    trec_license_number: "689214",
  },
  {
    id: "u-3",
    email: "priya@lonestar.example",
    full_name: "Priya Nair",
    phone_e164: "+15125550188",
    primary_role: "agent",
    status: "ACTIVE",
    last_login_at: days(-5),
    created_at: days(-90),
    agent_id: "20000000-0000-0000-0000-000000000002",
    license_count: 2,
    nmls_id: "1102845",
    trec_license_number: "714903",
  },
  {
    id: "u-4",
    email: "sam@lonestar.example",
    full_name: "Sam Okafor",
    phone_e164: null,
    primary_role: "agent",
    status: "SUSPENDED",
    last_login_at: days(-40),
    created_at: days(-60),
    agent_id: "20000000-0000-0000-0000-000000000003",
    license_count: 0,
    nmls_id: null,
    trec_license_number: null,
  },
];

const DIDS = [
  {
    id: "did-1",
    tenant_id: TENANT_ID,
    agent_id: AGENT_ID,
    user_id: "u-2",
    did: "+12569466324",
    label: "Jordan direct",
    display_text: "+1 25-MYHOME-24",
    active: true,
    created_at: days(-120),
    updated_at: days(-120),
  },
];

// Mutable for the same reason as UNASSIGNED — the assignment has to land on
// the client row too, or the lower list keeps showing "Unassigned".
let CLIENTS = [
  {
    id: "c-1",
    first_name: "Maria",
    last_name: "Delgado",
    email: "maria.delgado@example.com",
    phone_e164: "+15125550142",
    assigned_agent_id: AGENT_ID,
    tenant_id: TENANT_ID,
  },
  {
    id: "c-2",
    first_name: "Owen",
    last_name: "Fischer",
    email: "owen.fischer@example.com",
    phone_e164: "+15125550176",
    assigned_agent_id: AGENT_ID,
    tenant_id: TENANT_ID,
  },
  {
    id: "c-3",
    first_name: "Lena",
    last_name: "Park",
    email: "lena.park@example.com",
    phone_e164: "+15125550120",
    assigned_agent_id: "20000000-0000-0000-0000-000000000002",
    tenant_id: TENANT_ID,
  },
  {
    id: "c-4",
    first_name: "Darnell",
    last_name: "Ruiz",
    email: "darnell.ruiz@example.com",
    phone_e164: "+15125550133",
    assigned_agent_id: null,
    tenant_id: TENANT_ID,
  },
  {
    id: "c-5",
    first_name: "Aiko",
    last_name: "Tanaka",
    email: "aiko.tanaka@example.com",
    phone_e164: "+15125550155",
    assigned_agent_id: null,
    tenant_id: TENANT_ID,
  },
];

/**
 * ClientDetailOut-shaped record for the client-detail page. The list rows only
 * carry name/email/agent, so the detail view needs the address, contact, and
 * co-client fields fleshed out — and crucially `created_at`, which the page
 * slices for "Client since" (a missing value crashed the render).
 */
function clientDetail(c: (typeof CLIENTS)[number]) {
  const perClient: Record<string, Record<string, unknown>> = {
    "c-1": {
      phone_e164: "+15125550142",
      address_street: "4210 Red Oak Trail",
      address_city: "Round Rock",
      address_state: "TX",
      address_zip: "78681",
      marital_status: "married",
      employment_status: "employed",
      co_client_first_name: "Diego",
      co_client_last_name: "Delgado",
      co_client_email: "diego.delgado@example.com",
      co_client_phone_e164: "+15125550188",
      co_client_relationship: "spouse",
    },
    "c-2": {
      phone_e164: "+15125550176",
      address_street: "18 Barton Skyway",
      address_city: "Austin",
      address_state: "TX",
      address_zip: "78746",
      marital_status: "single",
      employment_status: "self_employed",
    },
  };
  return {
    id: c.id,
    tenant_id: c.tenant_id,
    first_name: c.first_name,
    last_name: c.last_name,
    email: c.email,
    assigned_agent_id: c.assigned_agent_id,
    phone_e164: null,
    address_street: null,
    address_city: null,
    address_state: null,
    address_zip: null,
    marital_status: null,
    employment_status: null,
    created_at: days(-30),
    co_client_first_name: null,
    co_client_last_name: null,
    co_client_email: null,
    co_client_phone_e164: null,
    co_client_relationship: null,
    ...(perClient[c.id] ?? {}),
    dossiers: DOSSIERS.filter((d) => d.client_id === c.id),
  };
}

// Mutable: assigning an agent removes the row here, which is the ONLY thing
// the triage banner counts. `const` made the assign a silent no-op — the row
// and the count both sat still while the request "succeeded" against the
// terminal fallback (audit B1).
let UNASSIGNED = [
  {
    id: "c-4",
    first_name: "Darnell",
    last_name: "Ruiz",
    email: "darnell.ruiz@example.com",
    created_at: days(-1),
    overdue: false,
    minutes_remaining: 72,
  },
  {
    id: "c-5",
    first_name: "Aiko",
    last_name: "Tanaka",
    email: "aiko.tanaka@example.com",
    created_at: days(-1),
    overdue: true,
    minutes_remaining: 0,
  },
];

/**
 * Slot detail per dossier — GET /document/dossiers/{id}.
 *
 * T004/F2: this was NOT merely unstubbed-and-empty. The terminal fallback
 * returns `{}`, which is truthy, so `Active.tsx:524` passed its
 * `detailQuery.data &&` guard and then ran `.slots.map` on `undefined` —
 * clicking "Slots" threw and the ErrorBoundary took the card. The stub is what
 * makes the screen render at all. Mutable so an approval is observable.
 *
 * The first dossier carries an AWAITING_APPROVAL slot; that is the one the
 * approve/reject actions act on.
 */
const dossierSlot = (o: Record<string, unknown>) => ({
  id: `sl-${o.template_item_id ?? o.doc_type_key}`,
  template_item_id: "22222222-2222-2222-2222-222222222222",
  is_required: true,
  recency_days: null,
  display_order: 0,
  status: "PENDING",
  document_id: null,
  rejection_reason: null,
  uploaded_at: null,
  validated_at: null,
  ...o,
});

let DOSSIER_SLOTS: Record<string, Array<Record<string, unknown>>> = {
  "d-1": [
    dossierSlot({
      template_item_id: "ti-photo-id",
      doc_type_key: "photo_id",
      display_name: "Photo ID",
      status: "VALIDATED",
      document_id: "cd-1",
      display_order: 1,
    }),
    dossierSlot({
      // The one awaiting a human. `template_item_id` matches REVIEW_QUEUE's
      // first row so the two screens act on the same slot.
      template_item_id: "22222222-2222-2222-2222-222222222222",
      doc_type_key: "gov_id_drivers_license",
      display_name: "Driver's License",
      status: "AWAITING_APPROVAL",
      document_id: "33333333-3333-3333-3333-333333333333",
      display_order: 2,
      uploaded_at: days(0),
    }),
    dossierSlot({
      template_item_id: "ti-bank-stmt",
      doc_type_key: "bank_statement",
      display_name: "Bank statement",
      status: "PENDING",
      display_order: 3,
    }),
    // Spec 021 — the slots the client-page documents (CLIENT_DOCS cd-1…cd-4,
    // cd-7, cd-8) fill, each in the shape the write path produces. Without
    // them the client page had no verdict to show and no test could see one.
    dossierSlot({
      // slot_matcher.py:558 — a CLASSIFIED document that needs no human.
      template_item_id: "ti-1",
      doc_type_key: "bank_statement",
      display_name: "Bank statement (June)",
      status: "VALIDATED",
      document_id: "cd-1",
      display_order: 4,
      uploaded_at: days(-2),
      validated_at: days(-2),
    }),
    dossierSlot({
      // documents.py:323-327 — a per-slot upload reserves the slot; match_slots
      // skips anything not CLASSIFIED (slot_matcher.py:292), so it stays PENDING.
      template_item_id: "ti-2",
      doc_type_key: "pay_stub",
      display_name: "Pay stub",
      status: "PENDING",
      document_id: "cd-2",
      display_order: 5,
    }),
    dossierSlot({
      // Same as ti-2: DECLARED_MISMATCH never reaches match_slots.
      template_item_id: "ti-3",
      doc_type_key: "government_id",
      display_name: "Government ID",
      status: "PENDING",
      document_id: "cd-3",
      display_order: 6,
    }),
    dossierSlot({
      // slot_matcher.py:174/:560 — the AI's verdict on a CLASSIFIED document
      // with fields missing (Gaurav's dev shape, document 669461b0).
      template_item_id: "ti-4",
      doc_type_key: "w2",
      display_name: "W-2",
      status: "REJECTED",
      document_id: "cd-4",
      rejection_reason: "Missing required fields: employer_name, wages",
      display_order: 7,
      uploaded_at: days(-3),
    }),
    dossierSlot({
      // slot_matcher.py:548-555 — the V074 path: declared type nobody
      // confirmed, so AWAITING_APPROVAL with NO reason.
      template_item_id: "ti-7",
      doc_type_key: "employer_letter",
      display_name: "Employer verification letter",
      status: "AWAITING_APPROVAL",
      document_id: "cd-7",
      display_order: 8,
      uploaded_at: days(-1),
    }),
    dossierSlot({
      // slot_matcher.py:454-460 — the identity path: the only AWAITING_APPROVAL
      // that carries a reason.
      template_item_id: "ti-8",
      doc_type_key: "passport",
      display_name: "Passport",
      status: "AWAITING_APPROVAL",
      document_id: "cd-8",
      rejection_reason:
        "We couldn't match the name on this document to your account. Your agent will review it.",
      display_order: 9,
      uploaded_at: days(0),
    }),
  ],
};

// Mutable: an approval has to move `slots_validated`. AGENT_APPROVED is a
// SATISFYING status server-side (models/dossier.py:31), so the card's counter
// moves — and that counter is the only ACCESSIBLE evidence of the approval.
// The slot pill's colour is a CSS class (`rp-pill--success`) with no aria
// marker, and the locator policy forbids asserting on classes.
let DOSSIERS = [
  {
    id: "d-1",
    client_id: "c-1",
    client_name: "Maria Delgado",
    client_email: "maria.delgado@example.com",
    client_phone_e164: "+15125550142",
    template_display_name: "Buyer representation",
    transaction_type: "showing",
    status: "IN_PROGRESS",
    slots_total: 12,
    slots_validated: 7,
    application_number: 1,
    due_date: days(9),
    assigned_agent_id: AGENT_ID,
    created_at: days(-3),
  },
  {
    id: "d-2",
    client_id: "c-2",
    client_name: "Owen Fischer",
    client_email: "owen.fischer@example.com",
    client_phone_e164: "+15125550176",
    template_display_name: "Mortgage pre-approval",
    transaction_type: "mortgage_application",
    status: "PENDING",
    slots_total: 6,
    slots_validated: 2,
    application_number: 2,
    due_date: days(12),
    assigned_agent_id: AGENT_ID,
    created_at: days(-6),
  },
  {
    id: "d-3",
    client_id: "c-3",
    client_name: "Lena Park",
    client_email: "lena.park@example.com",
    client_phone_e164: "+15125550120",
    template_display_name: "Seller listing",
    transaction_type: "listing",
    status: "COMPLETED",
    slots_total: 8,
    slots_validated: 8,
    application_number: 1,
    due_date: days(-2),
    assigned_agent_id: AGENT_ID,
    created_at: days(-9),
  },
  {
    id: "d-4",
    client_id: "c-4",
    client_name: "Darnell Ruiz",
    client_email: "darnell.ruiz@example.com",
    client_phone_e164: "+15125550133",
    template_display_name: "Pre-approval",
    transaction_type: "mortgage_application",
    status: "IN_PROGRESS",
    slots_total: 5,
    slots_validated: 4,
    application_number: 1,
    due_date: days(21),
    assigned_agent_id: AGENT_ID,
    created_at: days(-12),
  },
];

/**
 * GET /admin/tenants → TenantWithPlan[] (api-client services/admin.ts).
 *
 * `billing_plan` is a BillingPlanOut — the roster reads `plan_type`, NOT the
 * name/price this once carried, so every one of these cards said "no plan"
 * while claiming to have one. support_email/primary_phone come from the
 * brokerage profile and are null until it is filled, which is why the PENDING
 * one below leaves them out.
 */
const TENANTS = [
  {
    id: TENANT_ID,
    display_name: "Lone Star Realty",
    slug: "lonestar",
    status: "ACTIVE",
    brokerage_type: "REAL_ESTATE",
    created_at: days(-300),
    support_email: "hello@lonestar.example",
    primary_phone: "+15125550142",
    billing_plan: { id: "bp-1", plan_type: "PER_AGENT", agent_fee: "49.00" },
  },
  {
    id: "t-2",
    display_name: "Bluebonnet Mortgage",
    slug: "bluebonnet",
    status: "ACTIVE",
    brokerage_type: "MORTGAGE",
    created_at: days(-120),
    support_email: "team@bluebonnet.example",
    primary_phone: "+15125550188",
    billing_plan: { id: "bp-2", plan_type: "FLAT_RATE", flat_rate: "99.00" },
  },
  {
    id: "t-3",
    display_name: "Hill Country Homes",
    slug: "hillcountry",
    status: "PENDING",
    brokerage_type: "REAL_ESTATE",
    created_at: days(-4),
    support_email: null,
    primary_phone: null,
    billing_plan: null,
  },
];

const DOMAINS = [
  {
    id: "dom-1",
    domain: "app.lonestarrealty.com",
    rp_id: "lonestarrealty.com",
    is_primary: true,
    verified_at: days(-200),
  },
  {
    id: "dom-2",
    domain: "portal.lonestarrealty.com",
    rp_id: "lonestarrealty.com",
    is_primary: false,
    verified_at: null,
  },
];

/**
 * A HELD lease listing agreement, mid-completion — the state an agent actually
 * meets after V093, and the one the workspace exists to make survivable.
 *
 * Deliberately covers the four cases the screen has to get right, because a
 * fixture where everything is a filled text field proves nothing:
 *
 *   ¶5.G  broker_county = "NA"  -> BLOCKS, while looking filled. The gate treats
 *         the placeholder as blank; a screen that trusted the value would show a
 *         complete form beside a send button that refuses.
 *   ¶12.E rep_except_loans = "" -> BLOCKS, and looks blank. The honest case.
 *   ¶9.A  elect_intermediary   -> a CHECKBOX ticked by the template's default,
 *         so its stored value is empty and it does NOT block.
 *   client_full_name           -> no paragraph at all, so it lands in the
 *         trailing "Other fields" group exactly as it did before.
 *
 * Paragraph order is also deliberately scrambled here (12 before 5 before 2) so
 * the numeric-aware sort has something to do — a plain string sort would leave
 * ¶12.E above ¶2.B and ¶5.A.
 */
const FORM_FIELDS = [
  {
    field_name: "rep_except_loans",
    field_value: "",
    source: "auto_extracted",
    source_document_id: null,
    source_document_filename: null,
    updated_at: days(-1),
    paragraph_label: "12.E",
    field_kind: "text",
    is_required: true,
    blocks_send: true,
    effective_value: "",
  },
  {
    field_name: "client_full_name",
    field_value: "Marisol Okonkwo",
    source: "auto_extracted",
    source_document_id: null,
    source_document_filename: null,
    updated_at: days(-1),
  },
  {
    field_name: "broker_county",
    field_value: "NA",
    source: "auto_extracted",
    source_document_id: null,
    source_document_filename: null,
    updated_at: days(-1),
    paragraph_label: "5.G",
    field_kind: "text",
    is_required: true,
    blocks_send: true,
    effective_value: "NA",
  },
  {
    field_name: "broker_fee_lease_pct",
    field_value: "100",
    source: "auto_extracted",
    source_document_id: null,
    source_document_filename: null,
    updated_at: days(-1),
    paragraph_label: "5.A(1)(a)",
    field_kind: "text",
    is_required: true,
    blocks_send: false,
    effective_value: "100",
  },
  {
    field_name: "elect_fee_month_rent",
    field_value: "",
    source: "auto_extracted",
    source_document_id: null,
    source_document_filename: null,
    updated_at: days(-1),
    paragraph_label: "5.A(1)(a)",
    field_kind: "checkbox",
    is_required: true,
    blocks_send: false,
    effective_value: "X",
  },
  {
    field_name: "elect_intermediary",
    field_value: "",
    source: "auto_extracted",
    source_document_id: null,
    source_document_filename: null,
    updated_at: days(-1),
    paragraph_label: "9.A",
    field_kind: "checkbox",
    is_required: true,
    blocks_send: false,
    effective_value: "X",
  },
  {
    field_name: "listing_non_realty_items",
    field_value: "None",
    source: "auto_extracted",
    source_document_id: null,
    source_document_filename: null,
    updated_at: days(-1),
    paragraph_label: "2.B",
    field_kind: "text",
    is_required: false,
    blocks_send: false,
    effective_value: "None",
  },
  {
    field_name: "lease_req_guest_days",
    field_value: "7",
    source: "auto_extracted",
    source_document_id: null,
    source_document_filename: null,
    updated_at: days(-1),
    paragraph_label: "20.F",
    field_kind: "text",
    is_required: false,
    blocks_send: false,
    effective_value: "7",
  },
];

const FORM_DETAIL = {
  id: "f-4",
  client_id: "c-1",
  agent_id: null,
  form_type: "exclusive_right_to_lease",
  template_version: "TXR 1102",
  property_id: null,
  showing_id: null,
  packet_id: "p-1",
  status: "PREFILLED",
  docusign_envelope_id: null,
  signed_pdf_s3_key: null,
  created_at: days(-1),
  updated_at: days(-1),
  fields: FORM_FIELDS,
  property_address: "4701 Rancho Del Norte, McKinney, TX 75070",
};

const FORMS = [
  {
    id: "f-1",
    client_id: "c-1",
    form_type: "buyer_representation_agreement",
    status: "SIGNED",
    created_at: days(-10),
    updated_at: days(-8),
  },
  {
    id: "f-2",
    client_id: "c-2",
    form_type: "information_about_brokerage_services",
    status: "SENT_FOR_SIGNATURE",
    created_at: days(-3),
    updated_at: days(-3),
  },
  {
    id: "f-3",
    client_id: "c-3",
    form_type: "seller_disclosure_notice",
    status: "PREFILLED",
    created_at: days(-1),
    updated_at: days(-1),
  },
];

// Mutable so Confirm / Cancel / Show / No-show persist within a session.
let APPOINTMENTS: Array<Record<string, unknown>> = [
  {
    id: "a-1",
    status: "PENDING",
    appointment_type: "showing",
    property_address: "4218 Travis Heights Blvd",
    starts_at: "2026-07-24T14:00:00",
    client_id: null,
    client_name: "Jordan Mensah",
    agent_name: "Jordan Alvarez",
    created_at: days(-1),
    outcome: null,
  },
  {
    id: "a-2",
    status: "PENDING",
    appointment_type: "showing",
    property_address: "612 W Annie St",
    starts_at: "2026-07-24T17:30:00",
    client_id: null,
    client_name: "Marcus Bell",
    agent_name: "Jordan Alvarez",
    created_at: days(-2),
    outcome: null,
  },
  {
    id: "a-3",
    status: "CONFIRMED",
    appointment_type: "showing",
    property_address: "1200 Barton Springs Rd",
    starts_at: "2026-07-26T11:00:00",
    client_id: null,
    client_name: "Owen Fischer",
    agent_name: "Jordan Alvarez",
    created_at: days(-3),
    outcome: null,
  },
  {
    id: "a-4",
    status: "CONFIRMED",
    appointment_type: "showing",
    property_address: "908 E 5th St",
    starts_at: "2026-07-28T15:00:00",
    client_id: null,
    client_name: "Lena Park",
    agent_name: "Jordan Alvarez",
    created_at: days(-2),
    outcome: null,
  },
  {
    id: "a-6",
    status: "CONFIRMED",
    appointment_type: "showing",
    property_address: "77 Rainey St",
    starts_at: "2026-07-23T10:00:00",
    client_id: null,
    client_name: "Diego Ramos",
    agent_name: "Jordan Alvarez",
    created_at: days(-4),
    outcome: null,
  },
  {
    id: "a-5",
    status: "COMPLETED",
    appointment_type: "showing",
    property_address: "45 Rainey St",
    starts_at: "2026-07-20T10:00:00",
    client_id: null,
    client_name: "Aiko Tanaka",
    agent_name: "Jordan Alvarez",
    created_at: days(-6),
    outcome: "ATTENDED",
  },
  {
    id: "a-7",
    status: "COMPLETED",
    appointment_type: "showing",
    property_address: "300 W 6th St",
    starts_at: "2026-07-19T13:00:00",
    client_id: null,
    client_name: "Priya Shah",
    agent_name: "Jordan Alvarez",
    created_at: days(-7),
    outcome: "NO_SHOW",
  },
];

// Mutable so Reply / Later persist within a session.
let FOLLOWUPS: Array<Record<string, unknown>> = [
  {
    id: "fu-1",
    client_name: "Marcus Bell",
    topic: "question",
    details: "Can we push closing to end of month?",
    status: "NEW",
    agent_reply: null,
    replied_at: null,
    preferred_time: null,
    created_at: days(-1),
  },
  {
    id: "fu-3",
    client_name: "Maria Delgado",
    topic: "callback",
    details: "Please call me about the Oak Street listing.",
    status: "NEW",
    agent_reply: null,
    replied_at: null,
    preferred_time: "Weekday evenings",
    created_at: days(0),
  },
  {
    id: "fu-2",
    client_name: "Elena Ruiz",
    topic: "staging costs",
    details: "Any advice on staging before we list?",
    status: "CONTACTED",
    agent_reply: "Shared a staging vendor list and a rough budget.",
    replied_at: days(-1),
    preferred_time: null,
    created_at: days(-2),
  },
];

function adminProfile(role: Role) {
  const identity =
    role === "super_admin"
      ? {
          full_name: "Alex Chen",
          email: "alex@platform.example",
          display_title: "Platform Admin",
          scheduling_link: "cal.com/alex",
        }
      : role === "agent"
        ? {
            full_name: "Jordan Alvarez",
            email: "jordan@lonestar.example",
            display_title: "Broker / Owner",
            scheduling_link: "cal.com/jordan",
          }
        : {
            full_name: "Brenda Cole",
            email: "brenda@lonestar.example",
            display_title: "Broker / Admin",
            scheduling_link: "cal.com/brenda",
          };
  const base = {
    id: "mock-staff",
    ...identity,
    phone_e164: "+15125550142",
    primary_role: role,
    photo_url: null,
    // profile-svc fills these three for an AGENT only (me.py `_out`); everyone
    // else gets null (spec 023, T038).
    tenant_slug: null as string | null,
    signup_link_origin: null as string | null,
    public_phone_preference: "DID",
    did_shared: false,
  };
  if (role === "agent")
    return {
      ...base,
      tenant_slug: "lonestar",
      // A verified custom domain by default; `rp.mock.signupOrigin` = "none"
      // models an agent without one (the server's null).
      signup_link_origin: mockSignupOrigin(),
      agent_id: AGENT_ID,
      trec_license_number: "689214",
      did: "+1 (512) 555-0177",
      did_label: "Jordan Alvarez",
      did_display_text: "51-MYHOME-77",
      did_display_format: "VANITY",
    };
  return {
    ...base,
    agent_id: null,
    trec_license_number: null,
    did: null,
    did_label: null,
    did_display_text: null,
    did_display_format: null,
  };
}

// Mutable so profile edits, notification toggles, and passkey removals persist
// within a session (reset on reload / role switch).
let profileOverrides: Record<string, unknown> = {};
const notifState: Record<string, boolean> = {
  new_client_assigned: true,
  showing_requested: true,
  document_uploaded_validated: false,
  daily_activity_summary: true,
};
let passkeyState = [
  {
    id: "pk-1",
    friendly_name: "MacBook Pro · Touch ID",
    transports: ["internal"],
    created_at: days(-30),
    last_used_at: days(0),
  },
  {
    id: "pk-2",
    friendly_name: "iPhone · Face ID",
    transports: ["internal", "hybrid"],
    created_at: days(-12),
    last_used_at: days(-2),
  },
];

const AGENT_USAGE = {
  calls: { total: 38, ai_answered: 32, transferred: 6 },
  talk_time: { total_min: 142, avg_min_per_call: 3.7 },
  docs: { total: 147, auto_classified: 139, manual: 8 },
  recent: [
    {
      id: "act-1",
      kind: "call",
      title: "Inbound call — Marcus Bell",
      detail: "AI answered · 4m 12s · booked a showing",
      when: "2h ago",
    },
    {
      id: "act-2",
      kind: "doc",
      title: "Bank statement classified — Jordan Mensah",
      detail: "Auto-classified 98% · accepted",
      when: "5h ago",
    },
    {
      id: "act-3",
      kind: "reply",
      title: "AI drafted reply — Elena Ruiz",
      detail: "Staging cost question · you approved",
      when: "1d ago",
    },
  ],
};

// Only documents the AI could NOT confidently accept land here — low-confidence
// classifications, declared-type mismatches, or stale files. Cleanly classified
// docs are auto-accepted and never enter this queue.
// Shape mirrors ReviewQueueItemOut. A slot — not a document — is the unit of
// decision, so the ids the approval endpoint needs are the ids carried here.
// Mutable: deciding an item has to remove it, or /document-review shows the
// same row after an "Approved." toast and the test proves nothing (audit B3).
let REVIEW_QUEUE = [
  {
    dossier_id: "11111111-1111-1111-1111-111111111111",
    template_item_id: "22222222-2222-2222-2222-222222222222",
    document_id: "33333333-3333-3333-3333-333333333333",
    client_id: "44444444-4444-4444-4444-444444444444",
    client_name: "Jordan Mensah",
    application_number: 3,
    doc_type_key: "gov_id_drivers_license",
    display_name: "Driver's License",
    status: "AWAITING_APPROVAL",
    reason:
      "We couldn't match the name on this document to your account. Your agent will review it.",
    uploaded_at: new Date(Date.now() - 2 * 3600_000).toISOString(),
  },
  {
    dossier_id: "55555555-5555-5555-5555-555555555555",
    template_item_id: "66666666-6666-6666-6666-666666666666",
    document_id: "77777777-7777-7777-7777-777777777777",
    client_id: "88888888-8888-8888-8888-888888888888",
    client_name: "Marcus Bell",
    application_number: 1,
    doc_type_key: "paystub_30d",
    display_name: "Pay stub (last 30 days)",
    status: "REJECTED",
    reason: "Older than the 30 days this requirement allows.",
    uploaded_at: new Date(Date.now() - 26 * 3600_000).toISOString(),
  },
];

// Mutable so the client-detail approve/reject actions persist within a session.
// `dossier_id`/`template_item_id` are on the Document wire type as of V090's
// client-detail work — the server had never sent them, so the admin screen's
// "Re-upload" button read `undefined` off an untyped cast and silently did
// nothing. They are also what groups a document under its application here.
let CLIENT_DOCS: Array<Record<string, unknown>> = [
  {
    id: "cd-1",
    client_id: "c-1",
    original_filename: "bank_statement_june.pdf",
    doc_type: "bank_statement",
    status: "PROCESSED",
    classification_status: "CLASSIFIED",
    classification_method: "auto",
    classification_confidence: "0.980",
    upload_mode: "per_slot",
    s3_uploaded_at: days(-2),
    s3_key: "k1",
    mime_type: "application/pdf",
    size_bytes: 210000,
    created_at: days(-2),
    updated_at: days(-2),
    dossier_id: "d-1",
    template_item_id: "ti-1",
  },
  {
    id: "cd-2",
    client_id: "c-1",
    original_filename: "pay_stub_may.jpg",
    doc_type: "pay_stub",
    status: "PROCESSED",
    classification_status: "CLASSIFIED_PENDING_REVIEW",
    classification_method: "auto",
    classification_confidence: "0.610",
    upload_mode: "bulk_inbox",
    s3_uploaded_at: days(-1),
    s3_key: "k2",
    mime_type: "image/jpeg",
    size_bytes: 88000,
    created_at: days(-1),
    updated_at: days(-1),
    dossier_id: "d-1",
    template_item_id: "ti-2",
  },
  {
    id: "cd-3",
    client_id: "c-1",
    original_filename: "drivers_license.png",
    doc_type: "government_id",
    status: "PROCESSED",
    classification_status: "DECLARED_MISMATCH",
    classification_method: "declared",
    classification_confidence: "0.440",
    upload_mode: "per_slot",
    s3_uploaded_at: days(0),
    s3_key: "k3",
    mime_type: "image/png",
    size_bytes: 120000,
    created_at: days(0),
    updated_at: days(0),
    dossier_id: "d-1",
    template_item_id: "ti-3",
  },
  {
    id: "cd-4",
    client_id: "c-1",
    original_filename: "w2_2025.pdf",
    doc_type: "w2",
    status: "PROCESSED",
    classification_status: "CLASSIFIED",
    classification_method: "auto",
    classification_confidence: "0.950",
    upload_mode: "per_slot",
    s3_uploaded_at: days(-3),
    s3_key: "k4",
    mime_type: "application/pdf",
    size_bytes: 150000,
    created_at: days(-3),
    updated_at: days(-3),
    dossier_id: "d-1",
    template_item_id: "ti-4",
  },
  // Spec 021 — two documents waiting on the agent (DOSSIER_SLOTS ti-7, ti-8).
  // AWAITING_APPROVAL is only ever written for a CLASSIFIED document.
  {
    id: "cd-7",
    client_id: "c-1",
    original_filename: "employer_letter.pdf",
    doc_type: "employer_letter",
    status: "PROCESSED",
    classification_status: "CLASSIFIED",
    classification_method: "declared",
    classification_confidence: null,
    upload_mode: "per_slot",
    s3_uploaded_at: days(-1),
    s3_key: "k7",
    mime_type: "application/pdf",
    size_bytes: 64000,
    created_at: days(-1),
    updated_at: days(-1),
    dossier_id: "d-1",
    template_item_id: "ti-7",
  },
  {
    id: "cd-8",
    client_id: "c-1",
    original_filename: "passport.jpg",
    doc_type: "passport",
    status: "PROCESSED",
    classification_status: "CLASSIFIED",
    classification_method: "auto",
    classification_confidence: "0.930",
    upload_mode: "per_slot",
    s3_uploaded_at: days(0),
    s3_key: "k8",
    mime_type: "image/jpeg",
    size_bytes: 310000,
    created_at: days(0),
    updated_at: days(0),
    dossier_id: "d-1",
    template_item_id: "ti-8",
  },
  // Spec 017 — history: one of each way a document stops counting. Shapes are
  // what document-svc writes on discard (DISCARDED + why/when/role, the prior
  // status kept, no slot link any more).
  {
    id: "cd-5",
    client_id: "c-1",
    original_filename: "Chase_Bank_Statement.pdf",
    doc_type: "proof_of_funds",
    status: "DISCARDED",
    status_before_discard: "PROCESSED",
    discard_reason: "MISTAKE",
    discarded_at: days(-1),
    discarded_by_role: "client",
    replaced_by_document_id: null,
    classification_status: "DECLARED_MISMATCH",
    classification_method: "declared",
    classification_confidence: null,
    upload_mode: "per_slot",
    s3_uploaded_at: days(-2),
    s3_key: "k5",
    mime_type: "application/pdf",
    size_bytes: 99000,
    created_at: days(-2),
    updated_at: days(-1),
    dossier_id: null,
    template_item_id: null,
  },
  {
    id: "cd-6",
    client_id: "c-1",
    original_filename: "old_paystub.pdf",
    doc_type: "pay_stub",
    status: "DISCARDED",
    status_before_discard: "PROCESSED",
    discard_reason: "REPLACED",
    discarded_at: days(-4),
    discarded_by_role: "client",
    replaced_by_document_id: "cd-2",
    classification_status: "CLASSIFIED",
    classification_method: "declared",
    classification_confidence: null,
    upload_mode: "per_slot",
    s3_uploaded_at: days(-6),
    s3_key: "k6",
    mime_type: "application/pdf",
    size_bytes: 77000,
    created_at: days(-6),
    updated_at: days(-4),
    dossier_id: null,
    template_item_id: null,
  },
];

// ---- router -----------------------------------------------------------------

function mockSignupOrigin(): string | null {
  try {
    return localStorage.getItem("rp.mock.signupOrigin") === "none"
      ? null
      : "https://app.lonestarrealty.com";
  } catch {
    return "https://app.lonestarrealty.com";
  }
}

// ---- client invites (spec 023) ----------------------------------------------
// Rows as identity-svc persists them (client_invite_tokens), per tenant. The
// list route returns them in its ClientInvitePendingOut shape (`toPending`).
interface MockInvite {
  token: string;
  client_email: string | null;
  client_name: string | null;
  created_at: string;
  expires_at: string;
  used_at: string | null;
  revoked_at: string | null;
  resent_count: number;
  last_sent_at: string | null;
}

const invite = (
  token: string,
  email: string | null,
  name: string | null,
  expiresInDays: number,
  extra: Partial<MockInvite> = {},
): MockInvite => ({
  token,
  client_email: email,
  client_name: name,
  created_at: days(expiresInDays - 7),
  expires_at: days(expiresInDays),
  used_at: null,
  revoked_at: null,
  resent_count: 0,
  last_sent_at: email ? days(expiresInDays - 7) : null,
  ...extra,
});

const INVITES: Record<string, MockInvite[]> = {
  [TENANT_ID]: [
    invite("inv-maria", "maria.g@example.com", "Maria Garcia", 5),
    invite("inv-linkonly", null, null, 3),
    invite("inv-ben", "ben.t@example.com", "Ben Thompson", -2),
    invite("inv-used", "used@example.com", "Already Signed Up", 4, {
      used_at: days(-1),
    }),
  ],
  [SECOND_TENANT_ID]: [
    invite("inv-loan", "loan.client@example.com", "Priya Shah", 6),
  ],
};

function toPending(r: MockInvite) {
  return {
    token: r.token,
    invite_url: `https://app.newbrainllc.us/join?ref=${r.token}`,
    client_email: r.client_email,
    client_name: r.client_name,
    created_at: r.created_at,
    expires_at: r.expires_at,
    expired: new Date(r.expires_at).getTime() < Date.now(),
    resent_count: r.resent_count,
    last_sent_at: r.last_sent_at,
  };
}

/** Mirrors identity-svc (tenants.py, spec 023): agent-only; the list returns
 *  unused, unrevoked invites, expired ones INCLUDED and flagged; resend refuses
 *  revoked / used / email-less and re-opens an expired one for 7 days; revoke is
 *  own-tenant only (404 otherwise) and idempotent. */
function inviteRoute(
  method: string,
  path: string,
  role: Role,
  tenantId: string,
): unknown {
  if (!path.startsWith("/identity/client-invites")) return MISS_INVITE;
  if (role !== "agent")
    throw new ApiClientError("Requires role agent", 403, "forbidden", "mock");
  const rows = INVITES[tenantId] ?? [];
  if (method === "GET" && path === "/identity/client-invites")
    return rows
      .filter((r) => r.used_at === null && r.revoked_at === null)
      .map(toPending);
  const m = /^\/identity\/client-invites\/([^/]+)\/resend$/.exec(path);
  if (method === "POST" && m) {
    const row = rows.find((r) => r.token === decodeURIComponent(m[1] ?? ""));
    if (!row)
      throw new ApiClientError("Invite not found", 404, "not_found", "mock");
    if (row.revoked_at)
      throw new ApiClientError(
        "This invite was revoked.",
        409,
        "conflict",
        "mock",
      );
    if (row.used_at)
      throw new ApiClientError(
        "That invite has already been accepted — the client has an account.",
        409,
        "conflict",
        "mock",
      );
    if (!row.client_email)
      throw new ApiClientError(
        "This invite has no email address on file — copy the link and send it directly.",
        409,
        "conflict",
        "mock",
      );
    row.expires_at = days(7);
    row.resent_count += 1;
    row.last_sent_at = new Date().toISOString();
    return {
      token: row.token,
      invite_url: toPending(row).invite_url,
      expires_at: row.expires_at,
      resent_count: row.resent_count,
      email_queued: true,
    };
  }
  const rv = /^\/identity\/client-invites\/([^/]+)\/revoke$/.exec(path);
  if (method === "POST" && rv) {
    // Looked up in the ACTING tenant only — a request sent through the wrong
    // brokerage's client 404s, as the server's tenant-scoped session does.
    const row = rows.find((r) => r.token === decodeURIComponent(rv[1] ?? ""));
    if (!row)
      throw new ApiClientError("Invite not found", 404, "not_found", "mock");
    row.revoked_at ??= new Date().toISOString();
    return { token: row.token, revoked_at: row.revoked_at };
  }
  throw new ApiClientError("Not Found", 404, "not_found", "mock");
}
const MISS_INVITE = Symbol("miss-invite");

function route(
  method: string,
  rawPath: string,
  body?: unknown,
  query?: unknown,
  actingTenantId?: string | null,
): unknown {
  const path = rawPath.split("?")[0] ?? rawPath;
  const g = method === "GET";
  const role = mockRole();

  if (g && path === "/identity/memberships")
    return { memberships: memberships(role) };
  const inv = inviteRoute(method, path, role, actingTenantId ?? TENANT_ID);
  if (inv !== MISS_INVITE) return inv;
  if (g && path === "/admin/notifications/summary")
    return { followups: { new: 1 }, appointments: { pending: 1 }, total: 2 };
  if (g && path === "/admin/staff") return STAFF;
  if (g && path === "/admin/staff/invitations") return [];
  if (g && path === "/document/clients") return CLIENTS;
  if (g && path === "/document/clients/unassigned") return UNASSIGNED;
  // Assign (or clear) a client's agent. The router is a flat if-chain, so this
  // branch only has to precede the terminal fallback; `g` is a GET boolean and
  // never sees a PATCH. It fell through before purely because it did not exist.
  if (method === "PATCH" && /^\/document\/clients\/[^/]+$/.test(path)) {
    const id = path.split("/").pop();
    const b = (body as Record<string, unknown>) ?? {};
    const agentId = (b.assigned_agent_id as string | null) ?? null;
    CLIENTS = CLIENTS.map((c) =>
      c.id === id ? { ...c, assigned_agent_id: agentId } : c,
    );
    // Only an ASSIGNMENT clears the queue. Clearing an agent (null) must leave
    // the row in place, or "unassign" would make the client disappear from the
    // one screen whose job is to catch unassigned clients.
    if (agentId) UNASSIGNED = UNASSIGNED.filter((c) => c.id !== id);
    return CLIENTS.find((c) => c.id === id) ?? {};
  }
  if (g && /^\/document\/clients\/[^/]+$/.test(path)) {
    const id = path.split("/").pop();
    const c = CLIENTS.find((x) => x.id === id);
    if (!c)
      throw new ApiClientError("client not found", 404, "not_found", "mock");
    return clientDetail(c);
  }
  if (g && path === "/document/documents") {
    // Mirrors the server: DISCARDED only when staff ask for history.
    const q = (query as Record<string, unknown> | undefined) ?? {};
    return q.include_discarded
      ? CLIENT_DOCS
      : CLIENT_DOCS.filter((d) => d.status !== "DISCARDED");
  }
  // Spec 017 — the agent marks a client's upload "uploaded by mistake". Writes
  // what document-svc writes; the slot link goes with it.
  if (
    method === "POST" &&
    /^\/document\/documents\/[^/]+\/discard$/.test(path)
  ) {
    const id = path.split("/")[3];
    // discard.py:181-193 frees every slot the document filled — back to
    // PENDING with no document and no verdict — not only the document's link.
    DOSSIER_SLOTS = Object.fromEntries(
      Object.entries(DOSSIER_SLOTS).map(([dossierId, slots]) => [
        dossierId,
        slots.map((sl) =>
          sl.document_id === id
            ? {
                ...sl,
                document_id: null,
                status: "PENDING",
                rejection_reason: null,
                uploaded_at: null,
                validated_at: null,
              }
            : sl,
        ),
      ]),
    );
    CLIENT_DOCS = CLIENT_DOCS.map((d) =>
      d.id === id && d.status !== "DISCARDED"
        ? {
            ...d,
            status_before_discard: d.status,
            status: "DISCARDED",
            discard_reason: "MISTAKE",
            discarded_at: new Date().toISOString(),
            discarded_by_role: role === "agent" ? "agent" : role,
            dossier_id: null,
            template_item_id: null,
          }
        : d,
    );
    return CLIENT_DOCS.find((d) => d.id === id) ?? {};
  }
  // Approve a flagged document — relabel / confirm_declared both resolve to
  // CLASSIFIED, matching document-svc's apply_classification_action.
  if (
    method === "POST" &&
    /^\/document\/documents\/[^/]+\/classification$/.test(path)
  ) {
    const id = path.split("/")[3];
    CLIENT_DOCS = CLIENT_DOCS.map((d) =>
      d.id === id
        ? { ...d, classification_status: "CLASSIFIED", updated_at: days(0) }
        : d,
    );
    return CLIENT_DOCS.find((d) => d.id === id) ?? {};
  }
  // Decide a slot awaiting human confirmation (V074). BOTH /dossiers/active
  // and /document-review POST here — DocumentReview.tsx:200 uses this endpoint,
  // not the classification one above.
  if (
    method === "POST" &&
    /^\/document\/dossiers\/[^/]+\/slots\/[^/]+\/approval$/.test(path)
  ) {
    const parts = path.split("/");
    const dossierId = parts[3] ?? "";
    const templateItemId = parts[5] ?? "";
    const b = (body as Record<string, unknown>) ?? {};
    const rejected = b.decision === "reject";
    // Spec 019: the decision names the document the agent saw. If the slot now
    // holds another one (the client replaced it), refuse — dossiers.py does the
    // same under the slot lock. Absent document_id: unchanged, as on the server.
    const current = (DOSSIER_SLOTS[dossierId] ?? []).find(
      (sl) => sl.template_item_id === templateItemId,
    );
    // Spec 021: dossiers.py:787-792 decides only AWAITING_APPROVAL or REJECTED
    // slots. The mock accepted anything, so a test could "reject" a PENDING
    // slot production refuses.
    if (
      current &&
      current.status !== "AWAITING_APPROVAL" &&
      current.status !== "REJECTED"
    )
      throw new ApiClientError(
        `That slot is ${String(current.status)}, not awaiting approval.`,
        409,
        "conflict",
        "mock",
      );
    if (
      typeof b.document_id === "string" &&
      current?.document_id &&
      current.document_id !== b.document_id
    )
      throw new ApiClientError(
        "The client replaced this document — review the new one.",
        409,
        "conflict",
        "mock",
      );
    // The decision is recorded on the SLOT alone, approve or reject — no server
    // code writes REJECTED_BY_HUMAN onto the document (spec 021 research R4).
    // T007a: approve had no branch at all, so the screen refetched and got the
    // same rows back — only the toast moved. This is where the "yes" lands.
    const wasSatisfying = (DOSSIER_SLOTS[dossierId] ?? []).some(
      (sl) =>
        sl.template_item_id === templateItemId &&
        (sl.status === "VALIDATED" || sl.status === "AGENT_APPROVED"),
    );
    DOSSIER_SLOTS = {
      ...DOSSIER_SLOTS,
      [dossierId]: (DOSSIER_SLOTS[dossierId] ?? []).map((sl) =>
        sl.template_item_id === templateItemId
          ? {
              ...sl,
              status: rejected ? "REJECTED" : "AGENT_APPROVED",
              rejection_reason: rejected
                ? ((b.reason as string) ??
                  "Not the right document for this requirement.")
                : null,
              validated_at: null,
            }
          : sl,
      ),
    };
    // Approving satisfies the requirement exactly as VALIDATED does, so the
    // dossier's validated count moves with it. Guarded on the previous status
    // so approving twice cannot inflate the count past the total.
    if (!rejected && !wasSatisfying) {
      DOSSIERS = DOSSIERS.map((d) =>
        d.id === dossierId
          ? { ...d, slots_validated: d.slots_validated + 1 }
          : d,
      );
    }
    // Decided either way, it leaves the review queue — that is what makes
    // /document-review observable (audit B3). Both screens POST here.
    REVIEW_QUEUE = REVIEW_QUEUE.filter(
      (r) => r.template_item_id !== templateItemId,
    );
    return {
      dossier_id: dossierId,
      template_item_id: templateItemId,
      status: b.decision === "reject" ? "REJECTED" : "APPROVED",
      dossier_completed: false,
    };
  }
  if (g && /^\/document\/dossiers\/[^/]+$/.test(path)) {
    const id = path.split("/").pop() ?? "";
    const d = DOSSIERS.find((x) => x.id === id);
    if (!d)
      throw new ApiClientError("dossier not found", 404, "not_found", "mock");
    return {
      ...d,
      template_id: `tpl-${id}`,
      template_key: "bra",
      transaction_id: `tx-${id}`,
      completed_at: null,
      loan_application_outstanding: false,
      // Every dossier gets an array — a missing key is what crashed the card.
      slots: DOSSIER_SLOTS[id] ?? [],
    };
  }
  if (g && path === "/document/dossiers") return DOSSIERS;
  if (g && path === "/admin/tenants") return TENANTS;
  if (g && /^\/admin\/tenants\/[^/]+\/domains$/.test(path)) return DOMAINS;
  if (g && path === "/trec/forms") return [...FORMS, FORM_DETAIL];
  if (g && /^\/trec\/forms\/[^/]+$/.test(path)) return FORM_DETAIL;
  if (g && path === "/appointment/requests") {
    const status = new URLSearchParams(rawPath.split("?")[1] ?? "").get(
      "status",
    );
    return status
      ? APPOINTMENTS.filter((a) => a.status === status)
      : APPOINTMENTS;
  }
  if (method === "PATCH" && /^\/appointment\/requests\/[^/]+$/.test(path)) {
    const id = path.split("/").pop();
    const b = (body as Record<string, unknown>) ?? {};
    APPOINTMENTS = APPOINTMENTS.map((a) =>
      a.id === id
        ? {
            ...a,
            status: b.status ?? a.status,
            outcome: b.outcome ?? a.outcome ?? null,
          }
        : a,
    );
    return APPOINTMENTS.find((a) => a.id === id) ?? {};
  }
  // Reply to a request — sets the agent reply + moves it to CONTACTED.
  if (
    method === "POST" &&
    /^\/voice-agent\/followups\/[^/]+\/reply$/.test(path)
  ) {
    const id = path.split("/")[3];
    const b = (body as Record<string, unknown>) ?? {};
    FOLLOWUPS = FOLLOWUPS.map((f) =>
      f.id === id
        ? {
            ...f,
            agent_reply: b.message,
            replied_at: days(0),
            status: "CONTACTED",
          }
        : f,
    );
    return FOLLOWUPS.find((f) => f.id === id) ?? {};
  }
  // Later / Mark contacted / Close — status transition.
  if (method === "PATCH" && /^\/voice-agent\/followups\/[^/]+$/.test(path)) {
    const id = path.split("/").pop();
    const b = (body as Record<string, unknown>) ?? {};
    FOLLOWUPS = FOLLOWUPS.map((f) =>
      f.id === id ? { ...f, status: b.status ?? f.status } : f,
    );
    return FOLLOWUPS.find((f) => f.id === id) ?? {};
  }
  if (g && path.startsWith("/voice-agent/followups")) {
    const m = rawPath.match(/status=([A-Z]+)/);
    return m ? FOLLOWUPS.filter((f) => f.status === m[1]) : FOLLOWUPS;
  }
  // ---- My account (V079) ----
  if (g && path === "/profile/me")
    return { ...adminProfile(role), ...profileOverrides };
  if (method === "PATCH" && path === "/profile/me") {
    Object.assign(profileOverrides, (body as Record<string, unknown>) ?? {});
    return { ...adminProfile(role), ...profileOverrides };
  }
  if (g && path === "/profile/me/notifications") return notifState;
  if (method === "PATCH" && path === "/profile/me/notifications") {
    Object.assign(notifState, (body as Record<string, boolean>) ?? {});
    return notifState;
  }
  if (g && path === "/identity/passkey/credentials") return passkeyState;
  if (method === "POST" && path === "/identity/passkey/device-link/mint")
    return {
      add_device_url: "https://app.lonestarrealty.com/add-device?t=mock-grant",
      expires_in: 900,
    };
  if (
    method === "DELETE" &&
    /^\/identity\/passkey\/credentials\/[^/]+$/.test(path)
  ) {
    const id = path.split("/").pop();
    passkeyState = passkeyState.filter((p) => p.id !== id);
    return undefined;
  }
  if (g && path === "/profile/me/usage") return AGENT_USAGE;
  if (g && path === "/document/review-queue") return REVIEW_QUEUE;

  if (g && path === "/admin/brokerage-settings") return MOCK_BROKERAGE;
  if (g && path === "/admin/config/dossier-templates") return TEMPLATES;
  if (g && path === "/admin/config/doc-types") return DOC_TYPES;
  if (g && path === "/admin/did-routes") return DIDS;
  if (g && /^\/admin\/agents\/[^/]+\/licenses$/.test(path)) return [];

  // Billing + seats (super_admin). These are ANNOTATED with the real response
  // types on purpose: the previous stubs were hand-written guesses and every
  // one of them was wrong in a way nothing could catch — seats returned
  // `agents: []` where an object was expected, so /staff crashed on
  // seats.admins.limit, and agent-costs returned `agents` where the screen
  // reads `rows`. An untyped `return {...}` in this file is a page crash
  // waiting to happen; keep new stubs annotated.
  if (g && path === "/admin/billing/catalog") return [];
  if (g && path === "/admin/staff/seats") return MOCK_SEATS;
  if (g && path === "/admin/billing/costs") return MOCK_PLATFORM_COSTS;
  if (g && path === "/admin/billing/margin") return marginFor(query);
  if (g && path === "/admin/billing/subscriptions") return SUBSCRIPTIONS;
  if (method === "POST" && path === "/admin/billing/subscriptions") {
    const b = (body ?? {}) as Partial<Subscription>;
    const amount = Number(b.amount ?? 0);
    const cycle = (b.billing_cycle ??
      "MONTHLY") as Subscription["billing_cycle"];
    const created: Subscription = {
      id: `sub-${Date.now()}`,
      tenant_id: b.tenant_id ?? null,
      tenant_name: b.tenant_id ? "Lone Star Realty" : null,
      vendor: b.vendor ?? "",
      name: b.name ?? "",
      category: b.category ?? null,
      amount: amount.toFixed(2),
      currency: b.currency ?? "USD",
      billing_cycle: cycle,
      active_from: b.active_from ?? new Date().toISOString().slice(0, 10),
      active_to: b.active_to ?? null,
      notes: b.notes ?? null,
      monthly_equivalent: (cycle === "ANNUAL"
        ? amount / 12
        : cycle === "ONE_OFF"
          ? 0
          : amount
      ).toFixed(2),
    };
    SUBSCRIPTIONS = [created, ...SUBSCRIPTIONS];
    return created;
  }
  if (/^\/admin\/billing\/subscriptions\/[^/]+$/.test(path)) {
    const id = path.split("/").pop()!;
    if (method === "DELETE") {
      SUBSCRIPTIONS = SUBSCRIPTIONS.filter((x) => x.id !== id);
      return {};
    }
    if (method === "PATCH") {
      const b = (body ?? {}) as Record<string, unknown>;
      SUBSCRIPTIONS = SUBSCRIPTIONS.map((x) =>
        x.id === id ? { ...x, ...(b as Partial<Subscription>) } : x,
      );
      return SUBSCRIPTIONS.find((x) => x.id === id)!;
    }
  }
  if (g && path === "/admin/billing/agent-costs") return MOCK_AGENT_COSTS;
  if (g && path === "/admin/billing/timeseries") return MOCK_TIMESERIES;

  // Deep DTI screen (agent) — clean empty state instead of a broken object.
  if (g && path.startsWith("/mortgage/dti"))
    throw new ApiClientError("No DTI snapshot yet", 404, "not_found", "mock");

  if (g) return [];
  return {};
}

// ---- install ----------------------------------------------------------------

let installed = false;

export function installAdminMocks(): void {
  if (installed || !mockEnabled()) return;
  installed = true;

  // Give the app a role-matching (fake, never-sent) token so the nav + gates
  // reflect the chosen role.
  try {
    localStorage.setItem("rp.admin.token", makeFakeToken(mockRole()));
  } catch {
    /* ignore */
  }

  (ApiClient.prototype as { request: unknown }).request = async function (
    this: { config?: { getActingTenantId?: () => string | null | undefined } },
    path: string,
    options: { method?: string; query?: unknown; body?: unknown } = {},
  ) {
    // The tenant a merged/dual-licensed view pinned this client to
    // (ApiClient.withActingTenant) — the X-Acting-Tenant-Id the server reads.
    const acting = this.config?.getActingTenantId?.() ?? null;
    return route(
      options.method ?? "GET",
      path,
      options.body,
      options.query,
      acting,
    );
  };
}
